import { useEffect, useState } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Copy, ShieldCheck, Warning } from "@phosphor-icons/react";

const PROVIDERS = [
  { value: "manual", label: "Manual — HR uploads BG reports themselves", needsKey: false },
  { value: "betterplace", label: "BetterPlace", needsKey: true,
    docs: "https://api.betterplace.co.in/v1/cases", help: "Get your API key from your BetterPlace dashboard → API." },
  { value: "hirright", label: "Hirright", needsKey: true, comingSoon: true },
  { value: "authbridge", label: "AuthBridge", needsKey: true, comingSoon: true },
];

export default function BGCheckSettings() {
  const [s, setS] = useState(null);
  const [provider, setProvider] = useState("manual");
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [defaultPackage, setDefaultPackage] = useState("STANDARD");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [moduleOff, setModuleOff] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/bg-check/settings");
      setS(data); setModuleOff(false);
      setProvider(data.provider || "manual");
      setEndpoint(data.api_endpoint || "");
      setDefaultPackage(data.default_package || "STANDARD");
      setEnabled(data.enabled ?? true);
    } catch (e) {
      if (e?.response?.status === 402) { setModuleOff(true); }
      else { toast.error("Failed to load settings"); }
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        company_id: "", provider, enabled,
        api_endpoint: endpoint || null,
        default_package: defaultPackage || null,
      };
      if (apiKey) body.api_key = apiKey;
      const { data } = await api.put("/bg-check/settings", body);
      toast.success("Saved");
      setApiKey(""); // clear the input — the server stored it
      setWebhookUrl(data.webhook_url || "");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  if (moduleOff) {
    return (
      <AppShell title="Background Verification">
        <SectionCard testid="section-bg-off">
          <div className="text-center py-16 px-6 max-w-2xl mx-auto">
            <ShieldCheck size={48} className="mx-auto text-zinc-300 mb-3"/>
            <div className="font-display text-2xl font-bold mb-2">Background Verification is off</div>
            <p className="text-sm text-zinc-600 mb-6">
              Enable this module to run BG checks on candidates before they join. Choose between manual (HR uploads reports) or a third-party vendor (BetterPlace, Hirright, AuthBridge). ₹25/employee/month retail.
            </p>
            <Button data-testid="bg-go-modules" onClick={() => window.location.href = "/app/billing"}>Enable in Module Settings →</Button>
          </div>
        </SectionCard>
      </AppShell>
    );
  }

  const prov = PROVIDERS.find(p => p.value === provider) || PROVIDERS[0];

  return (
    <AppShell title="Background Verification — Settings">
      <SectionCard title="Vendor configuration" subtitle="Pick the BG-check vendor or run it manually." testid="section-bg-vendor">
        <div className="space-y-4 max-w-2xl">
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="mt-1" data-testid="bg-provider"><SelectValue/></SelectTrigger>
              <SelectContent>
                {PROVIDERS.map(p => (
                  <SelectItem key={p.value} value={p.value} disabled={p.comingSoon}>
                    {p.label} {p.comingSoon && <span className="text-zinc-400 text-xs">(coming soon)</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {prov.help && <p className="text-xs text-zinc-500 mt-1">{prov.help}</p>}
          </div>

          {prov.needsKey && (
            <>
              <div>
                <Label className="text-xs">API Key {s?.api_key_set && <span className="text-emerald-600">(set — paste new key to replace)</span>}</Label>
                <Input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={s?.api_key_set ? "•••••••• (already configured)" : "Paste vendor API key"}
                  className="mt-1 font-mono"
                  data-testid="bg-api-key"
                />
              </div>
              <div>
                <Label className="text-xs">API Endpoint (optional)</Label>
                <Input
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder={prov.docs}
                  className="mt-1 font-mono text-xs"
                  data-testid="bg-endpoint"
                />
              </div>
              <div>
                <Label className="text-xs">Default package code</Label>
                <Input
                  value={defaultPackage}
                  onChange={(e) => setDefaultPackage(e.target.value)}
                  placeholder="e.g. STANDARD, COMPREHENSIVE"
                  className="mt-1 font-mono text-xs"
                  data-testid="bg-package"
                />
              </div>
            </>
          )}

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="bg-enabled"/>
            <div>
              <div className="font-medium">Module enabled</div>
              <div className="text-xs text-zinc-500">When off, new BG checks can't be triggered. Existing in-progress checks continue.</div>
            </div>
          </label>

          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={busy} data-testid="bg-save">{busy ? "Saving…" : "Save settings"}</Button>
            {prov.value === "manual" && <span className="text-xs text-zinc-500">Manual mode requires no vendor — save and you're done.</span>}
          </div>
        </div>
      </SectionCard>

      {(webhookUrl || s?.api_key_set) && prov.needsKey && (
        <SectionCard title="Webhook configuration" subtitle="Paste this URL into your vendor dashboard so they can post status updates back." testid="section-bg-webhook">
          <div className="space-y-3 max-w-3xl">
            <div>
              <Label className="text-xs">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input readOnly value={webhookUrl || `https://saarone.com/api/bg-check-webhook/${s?.company_id || ""}`} className="font-mono text-xs" data-testid="bg-webhook-url"/>
                <Button size="icon" variant="outline" onClick={() => copy(webhookUrl || `https://saarone.com/api/bg-check-webhook/${s?.company_id}`)} data-testid="bg-copy-url"><Copy size={14}/></Button>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 flex gap-2">
              <Warning size={16} className="text-amber-600 flex-shrink-0 mt-0.5"/>
              <div className="text-xs text-amber-900">
                <strong>Webhook secret was generated when you saved.</strong> Your vendor must include it in the <code className="bg-white px-1">X-Signature</code> header on every POST. Save it from your vendor dashboard's outgoing-webhook config — we only show it once when configuring the vendor.
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      <SectionCard title="Auto-block on red" subtitle="Stop offers from being converted to employees when BG check fails." testid="section-bg-block">
        <p className="text-sm text-zinc-700 max-w-2xl">
          When a candidate's BG check comes back <span className="text-rose-700 font-medium">red</span>, the system refuses to convert them to an Employee. HR admins can override per-case by passing <code className="bg-zinc-100 px-1 rounded">force_convert: true</code> — every override is audit-logged.
        </p>
      </SectionCard>
    </AppShell>
  );
}
